import type { ReactNode } from 'react'
import { View, type ViewProps } from 'react-native'
import Icon, { type IconName } from '~/components/Icon.tsx'
import { Body } from '~/components/typography/Content.tsx'
import { Heading3 } from '~/components/typography/Titles.tsx'
import designTokens from '~/lib/designTokens.ts'
import { cn } from '~/lib/utils'

export interface ValueItem {
  icon?: IconName
  value: ReactNode
  description?: ReactNode
  children?: ReactNode
  suffix?: string
  showLabel?: boolean
  className?: string
  contentClassName?: string
  labelClassName?: string
  valueClassName?: string
}

interface ValueListItemProps extends ViewProps {
  contentClassName?: string
  labelClassName?: string
  valueClassName?: string
}

export interface ValueListProps extends Omit<ValueListItemProps, 'contentClassName'> {
  values: ValueItem[]
  itemClassName?: string
  itemContentClassName?: string
}

export const ValueList = ({
  values,
  itemClassName,
  labelClassName,
  valueClassName,
  itemContentClassName,
  className,
  ...props
}: ValueListProps) => {
  return (
    <View className={cn('w-full', className)} {...props}>
      {values.map((item, index) => (
        <ValueListItem
          key={`${String(item.value)}-${index}`}
          item={item}
          className={cn(itemClassName, index === values.length - 1 && 'border-b-0')}
          contentClassName={itemContentClassName}
          labelClassName={labelClassName}
          valueClassName={valueClassName}
        />
      ))}
    </View>
  )
}

interface ValueListItemComponentProps extends ValueListItemProps {
  item: ValueItem
}

export const ValueListItem = ({
  item,
  className,
  contentClassName,
  labelClassName,
  valueClassName,
  ...props
}: ValueListItemComponentProps) => {
  const {
    icon: iconName,
    value,
    description,
    children,
    suffix,
    showLabel = true,
    className: itemClassName,
    contentClassName: itemContentClassName,
    labelClassName: itemLabelClassName,
    valueClassName: itemValueClassName,
  } = item

  const renderValue = () => {
    if (!showLabel) {
      return null
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return (
        <Heading3 className={cn('leading-none', labelClassName, itemLabelClassName)}>
          {value}
          {suffix ? ` ${suffix}` : ''}
        </Heading3>
      )
    }

    return value
  }

  const renderSubValue = () => {
    if (!description) {
      return null
    }

    if (typeof description === 'string' || typeof description === 'number') {
      return (
        <Body className={cn('shrink-0', valueClassName, itemValueClassName)}>{description}</Body>
      )
    }

    return description
  }

  return (
    <View
      className={cn('flex-row gap-4 border-b border-border px-4 py-5', className, itemClassName)}
      {...props}>
      {iconName && (
        <View
          className={cn(
            '-mt-px items-start justify-start self-start',
            !description && 'self-center'
          )}>
          <Icon
            name={iconName}
            size={20}
            className="text-foreground"
            color={designTokens.foreground}
          />
        </View>
      )}
      <View className={cn('min-h-0 flex-1 flex-col gap-1', contentClassName, itemContentClassName)}>
        {renderValue()}
        {renderSubValue()}
        {children}
      </View>
    </View>
  )
}
